import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DebugCaptureRuleService } from 'src/app/shared/services/debug-capture-rule.service';
import { RolePermission, RolePermissionService } from '../auth/rbac/role-permission.service';

/**
 * NSR-11 AC: "The whole area is permission-gated because captures may contain sensitive data."
 * Unlike system-logs (nav-hide only, with an in-page reveal toggle for the sensitive bit), the
 * debug-capture module has no lower tier to fall back to - DEBUG_CAPTURE_MANAGE is the only
 * permission the backend defines for it (see role-permission.service.ts), so this guard blocks
 * the route itself as a second, independent gate beyond hiding the nav link.
 *
 * The FE's static role table has no visibility into a non-ADMIN user's direct backend grant for
 * this permission (vb-core's PermissionService.requirePermission supports per-user direct grants;
 * no role row carries DEBUG_CAPTURE_MANAGE - see role-permission.service.ts). Blocking on the
 * static check alone would hard-lock-out a legitimately authorized user with only a direct grant
 * (CodeRabbit, MR !46: "directly authorized users may be denied access"). Unlike system-logs'
 * mask-then-widen approach, a route guard has no content to show first and correct after the
 * fact, so when the static check fails this probes the real rule-list endpoint (cheap, size 1)
 * and lets the backend's own authorization be the final word instead of only trusting the FE's
 * role table.
 */
@Injectable()
export class DebugCaptureAccessGuard {

  constructor(
    private readonly rolePermissionService: RolePermissionService,
    private readonly ruleService: DebugCaptureRuleService,
    private readonly router: Router,
  ) {
  }

  canActivate(): Observable<boolean | UrlTree> {
    if (this.rolePermissionService.hasPermissions([RolePermission.DEBUG_CAPTURE_MANAGE])) {
      return of(true);
    }
    return this.ruleService.query({ page: 0, size: 1 }).pipe(
      map(() => true),
      catchError(() => of(this.router.createUrlTree(['/forbidden']))),
    );
  }
}
